import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { requireAuth } from "~/lib/auth-guard";
import { useResumeStore } from "~/stores/resume-store";
import { ResumeEditor } from "~/components/ResumeEditor";
import { ResumePreview } from "~/components/ResumePreview";
import { ResizableSplitPane } from "~/components/ResizableSplitPane";
import { AtsScoreBar } from "~/components/editor/AtsScoreBar";
import { EditWithAiFab } from "~/components/editor/EditWithAiFab";
import { SaveIndicator } from "~/components/editor/SaveIndicator";
import { DuplicateTranslate } from "~/components/editor/DuplicateTranslate";
import { TailorFromEditor } from "~/components/editor/TailorFromEditor";
import { useAutoSave } from "~/hooks/useAutoSave";
import { exportResumeDocx, exportResumePdf } from "~/lib/export";
import { exportResumeMarkdown } from "~/lib/export-markdown";
import { client } from "~/lib/orpc-client";
import { useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";
import { trackEvent } from "~/lib/posthog-client";

export const Route = createFileRoute("/resumes/$resumeId")({
  beforeLoad: async () => { await requireAuth(); },
  component: EditResumePage,
});

function EditResumePage() {
  const { resumeId } = Route.useParams();
  const navigate = useNavigate();
  // Scope the subscription to just this resume (+ the stable `syncFromServer`
  // action). A whole-store `useResumeStore()` here re-rendered the always-mounted
  // editor subtree — including the expensive `ResumePreview` — on every unrelated
  // resume-store write, the parent side of the ALCH-890 / FN-1476 freeze fan-out.
  const resume = useResumeStore((s) => s.resumes.find((r) => r.id === resumeId));
  const syncFromServer = useResumeStore((s) => s.syncFromServer);
  const { toast } = useToast();
  const saveStatus = useAutoSave(resumeId);
  const [exporting, setExporting] = React.useState(false);
  const [tailorOpen, setTailorOpen] = React.useState(false);
  const [serverLoading, setServerLoading] = React.useState(!resume);

  // Always fetch from server to ensure fresh data (locale, translated content, etc.)
  // Zustand persist may hold stale data from a previous session.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const serverResume = await client.resume.get({ id: resumeId });
        if (!cancelled && serverResume) {
          syncFromServer(serverResume);
        }
      } catch {
        // Resume doesn't exist on server — will show "not found" if not in store either
      } finally {
        if (!cancelled) setServerLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [resumeId]);

  if (serverLoading) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center animate-fade-in">
        <div className="card p-10 text-center" role="status" aria-live="polite">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--accent)]" />
          <p className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>{m.common_loading()}</p>
        </div>
      </div>
    );
  }

  if (!resume) {
    return (
      <div className="flex h-[calc(100vh-3.5rem)] items-center justify-center animate-fade-up">
        <div className="card p-10 text-center max-w-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl" style={{ background: "var(--error-subtle)" }}>
            <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" style={{ color: "var(--error)" }}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
            </svg>
          </div>
          <h2 className="font-semibold" style={{ fontSize: "var(--text-lg, 1.125rem)", color: "var(--text)" }}>
            {m.resumes_not_found()}
          </h2>
          <p className="mt-1.5 text-sm" style={{ color: "var(--text-muted)" }}>
            {m.resumes_not_found_desc()}
          </p>
          <button
            onClick={() => navigate({ to: "/resumes" })}
            className="btn btn-primary mt-5"
          >
            {m.resumes_back_to_resumes()}
          </button>
        </div>
      </div>
    );
  }

  async function exportPdf() {
    if (!resume) return;
    setExporting(true);
    try {
      const result = await exportResumePdf(resume.data, resume.title);
      if (result === "printer") {
        trackEvent("feature_used", { feature: "pdf_export", state: "completed" });
        toast(m.resumes_pdf_exported(), "success");
      } else {
        trackEvent("feature_used", { feature: "pdf_export_fallback", state: "completed" });
        toast("Printer service unavailable — use browser print → Save as PDF");
      }
    } catch {
      toast(m.resumes_pdf_export_failed(), "error");
    } finally {
      setExporting(false);
    }
  }

  async function exportDocx() {
    if (!resume) return;
    setExporting(true);
    try {
      await exportResumeDocx(resume.data, resume.title);
      toast(m.resumes_docx_exported(), "success");
    } catch {
      toast(m.resumes_docx_export_failed(), "error");
    } finally {
      setExporting(false);
    }
  }

  function exportMarkdown() {
    if (!resume) return;
    exportResumeMarkdown(resume.data, resume.title);
    trackEvent("feature_used", { feature: "markdown_export", state: "completed" });
    toast("Markdown exported", "success");
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)] flex-col animate-fade-up" data-testid="resume-editor">
      {/* Header bar */}
      <div className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--surface)] px-5 py-2.5">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate({ to: "/resumes" })}
            className="btn btn-ghost btn-sm"
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
            {m.common_back()}
          </button>
          <div className="h-5 w-px bg-[var(--border)]" />
          <h1 className="text-sm font-semibold">{resume.title}</h1>
          {resume.isMaster && (
            <span className="badge badge-accent">{m.common_master()}</span>
          )}
          <SaveIndicator status={saveStatus} />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTailorOpen(true)}
            className="btn btn-secondary btn-sm"
            data-testid="tailor-btn"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
            </svg>
            Tailor to Job
          </button>
          <DuplicateTranslate
            resumeId={resumeId}
            onNavigate={(newId) => navigate({ to: `/resumes/${newId}` })}
          />
          <div className="h-5 w-px bg-[var(--border)]" />
          <button
            onClick={exportPdf}
            disabled={exporting}
            data-testid="resume-export-pdf"
            className="btn btn-primary btn-sm"
          >
            {exporting ? (
              <span className="inline-block h-3 w-3 animate-spin rounded-full border border-current border-t-transparent mr-1" />
            ) : (
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="mr-1">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12M12 16.5V3" />
              </svg>
            )}
            PDF
          </button>
          <button
            onClick={exportDocx}
            disabled={exporting}
            data-testid="resume-export-docx"
            className="btn btn-secondary btn-sm"
          >
            {exporting ? (
              <span className="inline-block h-3 w-3 animate-spin rounded-full border border-current border-t-transparent mr-1" />
            ) : (
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="mr-1">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
              </svg>
            )}
            DOCX
          </button>
          <button
            onClick={exportMarkdown}
            className="btn btn-secondary btn-sm"
          >
            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" className="mr-1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
            <code className="text-[0.7rem] font-mono text-orange-500">{"{.md}"}</code>
          </button>
        </div>
      </div>

      {/* ATS Score Bar */}
      <div className="border-b border-[var(--border-subtle)] bg-[var(--surface)] px-5 py-2">
        <AtsScoreBar resumeId={resumeId} />
      </div>

      {/* Resizable split pane — 50/50 default with draggable divider */}
      <ResizableSplitPane
        left={<ResumeEditor resumeId={resume.id} />}
        right={
          <div className="relative h-full">
            <ResumePreview data={resume.data} />
            <EditWithAiFab resumeId={resume.id} />
          </div>
        }
      />

      {/* Tailor to Job modal */}
      <TailorFromEditor
        resumeId={resumeId}
        isOpen={tailorOpen}
        onClose={() => setTailorOpen(false)}
      />
    </div>
  );
}
